import {
  Body,
  Card,
  Flex,
  Glide,
  LazyImage,
  Row,
  TabItem,
  Tabs,
  Tag,
  Title,
  styled,
} from '@teko/ds-desktop';
import { formatCurrency } from '@teko/teko-ecommerce';
import { getTrackingData } from '@teko/tracking';
import NewComboPromotionImg from 'assets/combo-header-label.svg';
import ComboPromotionImg from 'assets/icon-combo-desktop.png';
import { useProductDetailState, useTenantConfig } from 'contexts';
import { useProductCombos } from 'hooks';
import { t } from 'i18n';
import { useEffect, useState } from 'react';
import { IActiveCombo } from 'types';
import { ComboItemNew } from './ComboItemNew';
import {
  IProductSelectionModalProps,
  ProductSelectionModal,
} from './ProductSelectionModal';

export const ComboCarouselNew: React.FC = () => {
  const { product } = useProductDetailState();
  const productComboHook = useProductCombos({
    skus: product?.sku ? [product.sku] : undefined,
  });
  const { activeCombo, setActiveCombo, comboGroup } = productComboHook;
  const { numberDisplayFormat, epicOmni1643 } = useTenantConfig();
  // activeTab state includes index of currently active tab and total products of each combo in active group
  const [activeTab, setActiveTab] = useState<{
    index: number;
    totalProductTab?: number;
  }>({
    index: 0,
  });
  // Index of selected slider in Glide on each tab
  const [displayedSlider, setDisplayedSlider] = useState<{
    [key: string]: number;
  }>();
  // State to keep product sku that user click to "Change product", state also keep both combo key & condition key containing product
  const [selectingProduct, setSelectingProduct] =
    useState<IProductSelectionModalProps['selectingProduct']>();

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // set initial activeTab value
    if (comboGroup && !activeTab.totalProductTab) {
      setActiveTab({
        index: 0,
        totalProductTab: Number(Object.keys(comboGroup)[0]),
      });
    }

    // set initial displayedSlider value
    if (comboGroup && !displayedSlider) {
      const value = Object.keys(comboGroup).reduce(
        (result: { [key: string]: number }, totalProduct: string) => ({
          ...result,
          [totalProduct]: 0,
        }),
        {},
      );
      setDisplayedSlider(value);
    }
  }, [JSON.stringify(comboGroup)]);

  return comboGroup ? (
    <>
      <Card
        header={
          <Row
            padding="s12"
            justify="start"
            align="middle"
            backgroundColor={epicOmni1643 ? 'secondary100' : 'primary100'}
            style={{ borderRadius: '8px 8px 0 0' }}
            height={epicOmni1643 ? 60 : 72}
          >
            <LazyImage
              src={epicOmni1643 ? NewComboPromotionImg : ComboPromotionImg}
              height={40}
              width={120}
            />
            <Title style={{ display: 'inline', marginLeft: '12px' }}>
              {t('Mua kèm nhận ưu đãi')}
            </Title>
          </Row>
        }
        border={epicOmni1643 ? '1px solid #00000026' : 'none'}
        margin={epicOmni1643 ? ['s12', 's0'] : 's0'}
      >
        <Tabs
          activeTab={activeTab.index}
          textColor="textSecondary"
          textActiveColor="primary"
        >
          {Object.entries(comboGroup).map(
            (
              [totalProduct, { combos }]: [string, { combos: IActiveCombo[] }],
              index,
            ) => {
              const discount =
                combos.length === 1
                  ? combos[0].discountByCombo
                  : combos[displayedSlider?.[totalProduct] || 0]
                      ?.discountByCombo;

              return (
                <TabItem
                  tabId={index}
                  key={index}
                  onClick={() => {
                    setActiveTab({
                      index,
                      totalProductTab: Number(totalProduct),
                    });
                  }}
                  icon={
                    <Flex
                      align="center"
                      direction="column"
                      padding={['s12', 's0']}
                      gap="s4"
                      height={64}
                    >
                      <Body
                        level={index === activeTab.index ? 2 : 1}
                        color={
                          index === activeTab.index
                            ? 'primary'
                            : 'textSecondary'
                        }
                      >
                        {t('Combo {{totalProduct}} sản phẩm', {
                          totalProduct,
                        })}
                      </Body>
                      {!!discount && (
                        <Tag
                          content={t('Giảm đến {{discount}}', {
                            discount: formatCurrency(
                              discount,
                              numberDisplayFormat,
                            ),
                          }).toUpperCase()}
                          type="ghost"
                          variant={
                            index === activeTab.index ? 'branding' : 'default'
                          }
                        />
                      )}
                    </Flex>
                  }
                  {...getTrackingData({
                    isTrackContent: true,
                    regionName: 'productComboList',
                    contentName: 'btnChangeComboTab',
                    payload: JSON.stringify({
                      comboTab: `Combo ${totalProduct} sản phẩm`,
                      comboIndex: index + 1,
                    }),
                  })}
                >
                  {combos.length === 1 ? (
                    <ComboItemNew
                      index={0}
                      combo={combos[0]}
                      onClickProductChange={(
                        selectedSku: string,
                        conditionKey: number,
                      ) => {
                        setVisible(true);
                        setSelectingProduct({
                          sku: selectedSku,
                          conditionKey,
                        });
                        setActiveCombo(combos[0]);
                      }}
                    />
                  ) : (
                    <GlideStyled
                      perView={1}
                      startAt={displayedSlider?.[totalProduct] || 0}
                      onSlideChange={(index: number) => {
                        if (typeof index === 'number' && index >= 0) {
                          setDisplayedSlider({
                            ...displayedSlider,
                            [totalProduct]: index,
                          });
                        }
                      }}
                      key={visible ? 1 : 0}
                      trackingArrowRight={getTrackingData({
                        isTrackContent: true,
                        regionName: 'productComboList',
                        contentName: 'btnChangeCombo',
                        payload: JSON.stringify({
                          comboTab: `Combo ${totalProduct} sản phẩm`,
                          comboPromotion:
                            combos[displayedSlider?.[totalProduct] || 0]
                              .promotion.id,
                          comboIndex:
                            (displayedSlider?.[totalProduct] || 0) + 1,
                          target: 'next',
                        }),
                      })}
                      trackingArrowLeft={getTrackingData({
                        isTrackContent: true,
                        regionName: 'productComboList',
                        contentName: 'btnChangeCombo',
                        payload: JSON.stringify({
                          comboTab: `Combo ${totalProduct} sản phẩm`,
                          comboPromotion:
                            combos[displayedSlider?.[totalProduct] || 0]
                              .promotion.id,
                          comboIndex:
                            (displayedSlider?.[totalProduct] || 0) + 1,
                          target: 'previous',
                        }),
                      })}
                    >
                      {combos.map((cb, index) => (
                        <ComboItemNew
                          key={index}
                          index={index}
                          combo={cb}
                          onClickProductChange={(
                            selectedSku: string,
                            conditionKey: number,
                          ) => {
                            setVisible(true);
                            setSelectingProduct({
                              sku: selectedSku,
                              conditionKey,
                            });
                            setActiveCombo(cb);
                          }}
                        />
                      ))}
                    </GlideStyled>
                  )}
                </TabItem>
              );
            },
          )}
        </Tabs>
      </Card>
      {visible && activeCombo && (
        <ProductSelectionModal
          closeModal={(selectingCombo?: IActiveCombo) => {
            setVisible(false);
            setSelectingProduct(undefined);
            // set active tab according to active combo
            if (selectingCombo) {
              const indexTab = Object.keys(comboGroup).findIndex(
                (item) => Number(item) === selectingCombo.totalProduct,
              );
              setActiveTab({
                index: indexTab,
                totalProductTab: selectingCombo.totalProduct,
              });
              if (comboGroup && activeTab.totalProductTab) {
                const activeGroup = comboGroup[activeTab.totalProductTab];
                const selectedIndex = activeGroup.combos.findIndex(
                  (el) => el.key === selectingCombo.key,
                );
                setDisplayedSlider({
                  ...displayedSlider,
                  [selectingCombo.totalProduct]: selectedIndex,
                });
              }
            }
          }}
          selectingProduct={selectingProduct}
          visible={visible}
          {...productComboHook}
        />
      )}
    </>
  ) : null;
};

const GlideStyled = styled(Glide)`
  .glide__arrow--override {
    visibility: visible;
  }
`;
